import {Box, Typography} from '@material-ui/core'
import React, {useEffect, useState} from 'react'
import {useParams} from 'react-router'

import {useLoggedInUserPermissions, useLoggedInUserProfile} from '../../hooks/useLoggedInUser'

import {useUserInformationFormData} from './hooks/useUserInformationFormData'
import {BlocklistNotificationsInput} from './inputs/BlocklistNotificationsInput'
import {TestUserInput} from './inputs/deprecated/TestUserInput'
import {UserBrandingInput} from './inputs/deprecated/UserBrandingInput'
import {UserCompanyInput} from './inputs/deprecated/UserCompanyInput'
import {UserCountryInput} from './inputs/deprecated/UserCountryInput'
import {UserEmailInput} from './inputs/deprecated/UserEmailInput'
import {UserLanguageInput} from './inputs/deprecated/UserLanguageInput'
import {UserMarketInput} from './inputs/deprecated/UserMarketInput'
import {UserMobileNumberInput} from './inputs/deprecated/UserMobileNumberInput'
import {UserNameInput} from './inputs/deprecated/UserNameInput'
import {UserOrgUnitInput} from './inputs/deprecated/UserOrgUnitInput'
import {TfaInput} from './inputs/TfaInput'
import {MessageHistory} from './MessageHistory/MessageHistory'
import {ProductActivity} from './ProductActivity/ProductActivity'
import {UserInformationInputSkeleton} from './Skeleton/UserInformationInputSkeleton'
import {useStyles} from './UserInformationForm.styles'
import {UserInformationFormProps} from './UserInformationForm.types'
import {UserInformationFormMode} from './utils/utils'

export const UserInformationForm = ({
  formMethods,
  formMode,
  readonlyMode,
  user,
  isLoading = false,
  tfaData,
  isTfaError,
  shouldShowTfaInput,
  loggedInUserPermissions,
  loggedInUserProfile
}: UserInformationFormProps) => {
  const classes = useStyles()
  const {userId} = useParams<{userId: string}>()
  const loggedInUserProfileData = useLoggedInUserProfile(loggedInUserProfile)
  const loggedInUserPermissionsData = useLoggedInUserPermissions(loggedInUserPermissions)
  const [showMfaInput, setShowMfaInput] = useState(shouldShowTfaInput)
  const values = formMethods.watch()
  const {
    brandings,
    countriesIds,
    createdByLink,
    createdByText,
    createdInText,
    createdOnText,
    createdViaText,
    marketName,
    marketSelectValues,
    primaryOrgUnitsMarketsSelectValues,
    primaryOrganisationalUnitLabel,
    selectedCountry
  } = useUserInformationFormData(values, user)

  useEffect(() => {
    formMethods.reset()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readonlyMode])

  const canEditUserEmailPhone =
    formMode === UserInformationFormMode.CREATE ||
    loggedInUserProfileData?.id === userId ||
    loggedInUserPermissionsData.some(
      (permission) => permission.permissionType === 'CHANGE_USER_LOGIN'
    )

  const canSeeMessageHistory = loggedInUserPermissionsData.some(
    (permission) => permission.permissionType === 'MANAGE_MESSAGES'
  )

  return (
    <Box className={classes.userInfoSection}>
      <div className={classes.form}>
        <UserNameInput
          name="name"
          readonlyMode={readonlyMode}
          formMethods={formMethods}
          isLoading={isLoading}
        />
        <UserEmailInput
          readonlyMode={readonlyMode}
          formMethods={formMethods}
          canEditUserEmailPhone={canEditUserEmailPhone}
          isLoading={isLoading}
          setShowMfaInput={setShowMfaInput}
          name="eMail"
        />
        <UserMobileNumberInput
          readonlyMode={readonlyMode}
          formMethods={formMethods}
          canEditUserEmailPhone={canEditUserEmailPhone}
          userMobileNumber={user?.mobileNumber}
          isLoading={isLoading}
          name="mobileNumber"
        />
        <UserCompanyInput
          readonlyMode={readonlyMode}
          formMethods={formMethods}
          isLoading={isLoading}
          name="companyName"
        />
        <UserCountryInput
          readonlyMode={readonlyMode}
          formMethods={formMethods}
          countriesIds={countriesIds}
          isLoading={isLoading}
          selectedCountry={selectedCountry}
          name="country"
        />
        <UserBrandingInput
          readonlyMode={readonlyMode}
          formMethods={formMethods}
          brandings={brandings}
          user={user}
          isLoading={isLoading}
          name="defaultBranding"
        />
        <UserOrgUnitInput
          readonlyMode={readonlyMode}
          formMethods={formMethods}
          primaryOrganisationalUnitLabel={primaryOrganisationalUnitLabel}
          primaryOrgUnitsMarketsSelectValues={primaryOrgUnitsMarketsSelectValues}
          isLoading={isLoading}
          name="primaryOrganisationalUnit"
        />
        <UserMarketInput
          readonlyMode={readonlyMode}
          formMethods={formMethods}
          marketName={marketName}
          marketSelectValues={marketSelectValues}
          isLoading={isLoading}
          name="marketId"
        />
        <UserLanguageInput
          readonlyMode={readonlyMode}
          formMethods={formMethods}
          selectedCountry={selectedCountry}
          isLoading={isLoading}
          name="defaultLocale"
        />
        <TestUserInput
          readonlyMode={readonlyMode}
          formMethods={formMethods}
          isTester={user?.isTester}
          isLoading={isLoading}
          name="isTester"
          loggedInUserProfile={loggedInUserProfileData}
        />
        {showMfaInput && !isTfaError && (
          <TfaInput
            formMethods={formMethods}
            isLoading={isLoading}
            tfaData={tfaData}
            name="twoFactorEnabled"
            loggedInUserProfile={loggedInUserProfileData}
          />
        )}
        <BlocklistNotificationsInput
          readonlyMode={readonlyMode}
          formMethods={formMethods}
          isLoading={isLoading}
          name="blocklistNotifications"
        />
        {isLoading ? (
          <UserInformationInputSkeleton single={true} firstRowWidth={600} />
        ) : (
          readonlyMode && (
            <Typography variant="caption" color="secondary">
              {createdOnText} {createdByText} {createdByLink} {createdViaText} {createdInText}
            </Typography>
          )
        )}
      </div>
      <div style={{display: 'flex', flexDirection: 'column', gap: '12px', flex: 1}}>
        <div>
          {formMode === UserInformationFormMode.MANAGE && (
            <ProductActivity lastActivityDates={user?.lastActivityDates} isLoading={isLoading} />
          )}
        </div>
        {formMode === UserInformationFormMode.MANAGE && canSeeMessageHistory && (
          <div data-test-id={`user-messages-${user?.user_id ?? ''}`}>
            <MessageHistory userId={user?.user_id || ''} />
          </div>
        )}
      </div>
    </Box>
  )
}
